'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import { Reveal } from '@/components/site/reveal'
import { SectionHead } from '@/components/site/section-head'
import { SiteSection } from '@/components/site/section'

export function ShareSection() {
  const { t } = useTranslation(['web'])
  return (
    <SiteSection trackingKey="share">
      <Reveal>
        <SectionHead
          eyebrow={t('web:meetingNotesPage.shareSection.eyebrow')}
          titleKey="web:meetingNotesPage.shareSection.title"
          lead={t('web:meetingNotesPage.shareSection.lead')}
        />
      </Reveal>
      <Reveal className="mt-(--gap-xl) grid grid-cols-3 gap-(--gap-lg) max-[860px]:grid-cols-1">
        <ShareCard
          art={<ExportArt />}
          aria={t('web:meetingNotesPage.shareSection.exportAria')}
          title={t('web:meetingNotesPage.shareSection.exportTitle')}
          description={t('web:meetingNotesPage.shareSection.exportDesc')}
        />
        <ShareCard
          art={<LinkArt />}
          aria={t('web:meetingNotesPage.shareSection.linkAria')}
          title={t('web:meetingNotesPage.shareSection.linkTitle')}
          description={t('web:meetingNotesPage.shareSection.linkDesc')}
        />
        <ShareCard
          art={<TeamArt />}
          aria={t('web:meetingNotesPage.shareSection.teamAria')}
          title={t('web:meetingNotesPage.shareSection.teamTitle')}
          description={t('web:meetingNotesPage.shareSection.teamDesc')}
        />
      </Reveal>
    </SiteSection>
  )
}
function ShareCard({
  art,
  aria,
  title,
  description,
}: {
  art: React.ReactNode
  aria: string
  title: string
  description: string
}) {
  return (
    <div>
      <div
        role="img"
        aria-label={aria}
        className="flex h-[272px] items-end justify-center border-b border-foreground px-2"
      >
        {art}
      </div>
      <h3 className="mt-4 text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-md leading-[1.65] text-muted">{description}</p>
    </div>
  )
}
function ExportArt() {
  const { t } = useTranslation(['web'])
  return (
    <div aria-hidden="true" className="relative h-[240px] w-[226px]">
      <div className="absolute bottom-0 left-0 h-[222px] w-[168px] origin-bottom-left -rotate-[5deg] overflow-hidden rounded-t border border-border border-b-0 bg-background p-[14px] font-mono text-[8.5px] leading-[1.7] text-muted">
        <span className="absolute top-[9px] right-[10px] text-[9px] tracking-[0.08em]">
          {t('web:meetingNotesPage.shareSection.demo.markdownFormat')}
        </span>
        <b className="font-medium text-foreground">
          # {t('web:meetingNotesPage.shareSection.demo.markdownTitle')}
        </b>
        <br />
        ## {t('web:meetingNotesPage.shareSection.demo.exportDecision')}
        <br />- {t('web:meetingNotesPage.shareSection.demo.markdownDecisionOne')}
        <br />- {t('web:meetingNotesPage.shareSection.demo.markdownDecisionTwo')}
        <br />
        ## {t('web:meetingNotesPage.shareSection.demo.exportTodo')}
        <br />- {t('web:meetingNotesPage.shareSection.demo.markdownTodo')}
      </div>
      <div className="absolute bottom-0 left-[22px] h-[222px] w-[168px] origin-bottom-left -rotate-[1.5deg] overflow-hidden rounded-t border border-border border-b-0 bg-background p-[14px]">
        <span className="absolute top-[9px] right-[10px] font-mono text-[9px] tracking-[0.08em] text-muted">
          {t('web:meetingNotesPage.shareSection.demo.wordFormat')}
        </span>
        <div className="mt-[18px] h-[5px] w-[58%] rounded-sm bg-line-soft" />
        <div className="mt-[7px] h-[5px] rounded-sm bg-line-soft" />
        <div className="mt-[7px] h-[5px] w-[84%] rounded-sm bg-line-soft" />
        <div className="mt-[7px] h-[5px] w-[70%] rounded-sm bg-line-soft" />
      </div>
      <div className="absolute right-0 bottom-0 h-[222px] w-[168px] overflow-hidden rounded-t border border-border border-b-0 bg-background p-[14px] font-display">
        <span className="absolute top-[9px] right-[10px] font-mono text-[9px] tracking-[0.08em] text-muted">
          {t('web:meetingNotesPage.shareSection.demo.pdfFormat')}
        </span>
        <p className="mt-[6px] mb-[5px] font-sans text-[7.5px] tracking-[0.12em] text-brand">
          {t('web:meetingNotesPage.shareSection.demo.exportTitle')}
        </p>
        <p data-display-font="" className="mb-1 text-[11px] leading-[1.35] font-bold">
          {t('web:meetingNotesPage.shareSection.demo.exportSubtitle')}
        </p>
        <p className="mb-[9px] font-sans text-[7px] text-muted">
          {t('web:meetingNotesPage.shareSection.demo.exportMeta')}
        </p>
        <PaperHeading i18nKey="exportDecision" />
        <PaperBullet i18nKey="pdfDecisionOne" />
        <PaperBullet i18nKey="pdfDecisionTwo" />
        <PaperHeading i18nKey="exportTodo" />
        <div className="grid grid-cols-[1fr_auto_auto] border-t border-foreground font-sans text-[7px] leading-[1.9]">
          {[
            'pdfTodoOne',
            'pdfTodoOneOwner',
            'pdfTodoOneDue',
            'pdfTodoTwo',
            'pdfTodoTwoOwner',
            'pdfTodoTwoDue',
          ].map((key, index) => (
            <span
              key={key}
              className={
                index % 3 === 0
                  ? 'border-b border-line-soft'
                  : 'border-b border-line-soft pl-2 text-muted'
              }
            >
              {t(`web:meetingNotesPage.shareSection.demo.${key}`)}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

function PaperHeading({ i18nKey }: { i18nKey: string }) {
  const { t } = useTranslation(['web'])
  return (
    <p data-display-font="" className="mt-2 mb-[3px] text-[8.5px] font-bold">
      {t(`web:meetingNotesPage.shareSection.demo.${i18nKey}`)}
    </p>
  )
}

function PaperBullet({ i18nKey }: { i18nKey: string }) {
  const { t } = useTranslation(['web'])
  return (
    <p
      data-display-font=""
      className="relative pl-[7px] text-[7.5px] leading-[1.6] before:absolute before:top-[0.72em] before:left-0 before:size-[3px] before:rounded-full before:bg-foreground"
    >
      {t(`web:meetingNotesPage.shareSection.demo.${i18nKey}`)}
    </p>
  )
}
function LinkArt() {
  const { t } = useTranslation(['web'])
  return (
    <div
      aria-hidden="true"
      className="mx-auto w-full max-w-[290px] rounded-t-[10px] border border-border border-b-0 bg-background px-4 pt-4 pb-[14px] text-sm shadow-(--shadow)"
    >
      <p className="font-semibold">{t('web:share.title')}</p>
      <p className="mt-2 text-xs leading-[1.6] text-muted">{t('web:share.subtitle')}</p>
      <p className="mt-4 text-xs text-muted">{t('web:share.versionLabel')}</p>
      <div className="mt-1 truncate border border-border px-2 py-2 text-xs">
        {t('web:meetingNotesPage.shareSection.demo.version')}
      </div>
      <p className="mt-3 text-xs text-muted">{t('web:share.linkLabel')}</p>
      <div className="mt-1 flex gap-2">
        <div className="min-w-0 flex-1 truncate border border-border px-2 py-2 text-xs">
          {t('web:meetingNotesPage.shareSection.demo.url')}
        </div>
        <span className="bg-foreground px-2 py-2 text-xs text-background">
          {t('web:share.copy')}
        </span>
      </div>
      <div className="mt-3 flex justify-between text-xs">
        <span>{t('web:share.updateSelected')}</span>
        <span>{t('web:share.revoke')}</span>
      </div>
    </div>
  )
}
function TeamArt() {
  const { t } = useTranslation(['web'])
  return (
    <div
      aria-hidden="true"
      className="mx-auto w-full max-w-[290px] rounded-t-[10px] border border-border border-b-0 bg-background px-4 pt-[14px] text-sm shadow-(--shadow)"
    >
      <p className="font-semibold">{t('web:meetingNotesPage.shareSection.demo.fileName')}</p>
      <p className="mt-1 text-xs text-muted">
        {t('web:meetingNotesPage.shareSection.demo.fileMeta')}
      </p>
      <div className="mt-4 flex justify-between border-y border-border py-3">
        <span>{t('web:share.teamToggle')}</span>
        <span className="relative h-5 w-9 rounded-full bg-foreground after:absolute after:top-1 after:right-1 after:size-3 after:rounded-full after:bg-background" />
      </div>
      <p className="mt-3 text-xs leading-[1.6] text-muted">{t('web:share.teamDescription')}</p>
      <p className="mt-4 text-xs">
        <b>{t('web:meetingNotesPage.shareSection.demo.cueSpeaker')}</b>{' '}
        {t('web:meetingNotesPage.shareSection.demo.cueTime')}{' '}
        {t('web:meetingNotesPage.shareSection.demo.cue')}
      </p>
    </div>
  )
}
